import { useEffect, useRef } from 'react';
import { LayoutDashboard, FileText, FileEdit, Clock, Timer, Play, Pause, Square, Bell, CalendarDays, BookOpen, FolderOpen, FileSignature, Building2, HardDrive, BookOpenCheck } from 'lucide-react';
import AiChat from '@/components/shared/AiChat';
import { useTimerStore } from '@/stores/timer.store';
import { api } from '@/lib/api';
import { formatHours } from '@/lib/utils';
import toast from 'react-hot-toast';
import WorkspaceShell from './WorkspaceShell';

const navItems = [
  { to: '/app/juriste/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', badge: false },
  { to: '/app/juriste/missions', icon: FileText, label: 'Missions', badge: false },
  { to: '/app/juriste/calendar', icon: CalendarDays, label: 'Calendrier', badge: false },
  { to: '/app/juriste/templates', icon: FileEdit, label: 'Modèles de documents', badge: false },
  { to: '/app/juriste/time', icon: Clock, label: 'Temps', badge: false },
  { to: '/app/juriste/lexbase',  icon: BookOpen,   label: 'VaegBase Vivant',     badge: false },
  { to: '/app/juriste/lexwork',      icon: FolderOpen,      label: 'Espace de travail',   badge: false },
  { to: '/app/juriste/drive',        icon: HardDrive,       label: 'VaegDrive',            badge: false },
  { to: '/app/juriste/lexcreate',    icon: Building2,       label: "Création d'entreprise", badge: false },
  { to: '/app/juriste/signatures',   icon: FileSignature,   label: 'Signatures',           badge: false },
  { to: '/app/juriste/aide',          icon: BookOpenCheck,  label: 'Mode d\'emploi',       badge: false },
  { to: '/app/juriste/notifications', icon: Bell,           label: 'Notifications',        badge: true },
];

function FloatingTimer() {
  const { isRunning, missionTitle, elapsed, startedAt, pause, resume, stop, description } = useTimerStore();
  const tickRef = useRef<ReturnType<typeof setInterval>>();

  const total = isRunning && startedAt
    ? elapsed + Math.floor((Date.now() - startedAt.getTime()) / 1000)
    : elapsed;

  useEffect(() => {
    if (isRunning) {
      tickRef.current = setInterval(() => useTimerStore.getState().tick(), 1000);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [isRunning]);

  const handleStop = async () => {
    const result = stop();
    if (!result) return;
    try {
      await api.post(`/missions/${result.missionId}/time-entries`, {
        description: description || 'Travail sur la mission',
        hours: result.hours,
        date: new Date().toISOString(),
        is_billable: true,
      });
      toast.success(`${formatHours(result.hours)} enregistrées`);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const fmt = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  if (!missionTitle && elapsed === 0) return null;

  return (
    <div className="workspace-timer" style={{
      position: 'fixed', bottom: 16, right: 16,
      background: '#1A1710', color: '#fff',
      borderRadius: 12, padding: '12px 14px',
      minWidth: 200, maxWidth: 260, width: 'calc(100vw - 32px)',
      zIndex: 50, boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Timer size={13} style={{ color: '#D4B866', flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{missionTitle}</span>
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, marginBottom: 10 }}>{fmt(total)}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {isRunning ? (
          <button onClick={pause} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#eab308', borderRadius: 8, fontSize: 12, fontWeight: 500, border: 'none', color: '#fff', cursor: 'pointer' }}>
            <Pause size={12} /> Pause
          </button>
        ) : (
          <button onClick={resume} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#22c55e', borderRadius: 8, fontSize: 12, fontWeight: 500, border: 'none', color: '#fff', cursor: 'pointer' }}>
            <Play size={12} /> Reprendre
          </button>
        )}
        <button onClick={handleStop} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#ef4444', borderRadius: 8, fontSize: 12, fontWeight: 500, border: 'none', color: '#fff', cursor: 'pointer' }}>
          <Square size={12} /> Arrêter
        </button>
      </div>
    </div>
  );
}

export default function JuristeLayout() {
  return <WorkspaceShell title="Espace juriste" items={navItems} quickPaths={['dashboard', 'missions', 'calendar', 'time']}><FloatingTimer /><AiChat /></WorkspaceShell>;
}
